import { Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInput } from '~/components/shared/DateInput'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { TimeInput } from '~/components/shared/TimeInput'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { DateRange, TimePeriodRange } from '~/types/Shared'

import {
  TimePeriod,
  tryGetMergedTimePeriodDate,
  tryGetTimePeriod,
} from '@mangovoice/core'
import { TimePeriodRange as CoreTimePeriodRange } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import {
  isKeyFromEditableField,
  isKeyFromNestedPopup,
} from '@mangovoice/mango-ui'

/** Controls whose own Enter action (Clear, Cancel, links) must not apply the range */
const OWN_ENTER_ACTION_SELECTOR = 'button, a[href], [role="button"], textarea'

enum PeriodName {
  After = 'after',
  Before = 'before',
}

interface PeriodGroupProps {
  label: string
  optionalText: string
  children: React.ReactNode
}

const PeriodGroup = ({ label, optionalText, children }: PeriodGroupProps) => {
  const labelId = useId()
  const optionalId = useId()

  return (
    <Flex
      role="group"
      aria-labelledby={labelId}
      aria-describedby={optionalId}
      direction="column"
    >
      <Flex
        data-testid="date-time-selection-header"
        alignSelf="stretch"
        alignItems="center"
        gap="1.25rem"
      >
        <Text {...textStyles[textStyleKeys.Body2_Bold]}>
          <span id={labelId}>{label}</span>{' '}
          <Text as="span" id={optionalId} {...textStyles[textStyleKeys.Body2]}>
            {optionalText}
          </Text>
        </Text>
      </Flex>
      <Flex
        data-testid="date-time-selection-wrapper"
        alignSelf="stretch"
        gap="1.25rem"
        py="0.5rem"
        alignItems="center"
      >
        {children}
      </Flex>
    </Flex>
  )
}

interface DateRangeModalProps {
  isOpen?: boolean
  dateTimeRange?: DateRange
  onClose?: () => void
  onClear?: () => void
  onSet?: (dateTimeRange: DateRange) => void
}

export const DateTimeRangeModal = ({
  isOpen,
  dateTimeRange: externalDateTimeRange,
  onClose,
  onClear,
  onSet,
}: DateRangeModalProps) => {
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateTimeRange ?? DateRange.empty(),
  )

  const [timeRange, setTimeRange] = useState<CoreTimePeriodRange>(
    TimePeriodRange.empty,
  )

  const setTimeRangeValue = (
    periodName: PeriodName,
    timePeriod?: TimePeriod,
  ) => {
    const newTimeRange: CoreTimePeriodRange = { ...timeRange }
    newTimeRange[periodName] = timePeriod

    setTimeRange(newTimeRange)
  }

  const setDateRangeValue = (period: PeriodName, value: Date | undefined) => {
    const newDateRange: DateRange = { ...dateRange }
    newDateRange[period] = value

    setDateRange(newDateRange)
  }

  const handleOnSet = () => {
    const newAfterDate = tryGetMergedTimePeriodDate(
      dateRange.after,
      timeRange.after,
    )

    const newBeforeDate = tryGetMergedTimePeriodDate(
      dateRange.before,
      timeRange.before,
    )

    onSet?.({
      after: newAfterDate,
      before: newBeforeDate,
    })

    // close modal
    onClose?.()
  }

  useEffect(() => {
    if (externalDateTimeRange) {
      const timePeriodRange = {
        before: tryGetTimePeriod(externalDateTimeRange.before),
        after: tryGetTimePeriod(externalDateTimeRange.after),
      }

      setTimeRange(timePeriodRange)
      setDateRange({ ...externalDateTimeRange })
    }
  }, [
    externalDateTimeRange,
    externalDateTimeRange?.before,
    externalDateTimeRange?.after,
  ])

  // Date/time segments commit their value on blur, so Enter in a field first
  // blurs it and applies the range once that edit has rendered.
  const [isApplyRequested, setIsApplyRequested] = useState(false)
  useEffect(() => {
    if (!isApplyRequested) return
    setIsApplyRequested(false)
    handleOnSet()
  }, [isApplyRequested])

  useKeyPresses({
    onPress: (e: KeyboardEvent) => {
      if (!isOpen || e.key !== 'Enter' || e.isComposing || e.defaultPrevented)
        return
      const target = e.target instanceof HTMLElement ? e.target : null
      if (!target?.closest('[role="dialog"]')) return
      if (isKeyFromNestedPopup(e)) return
      if (target.closest(OWN_ENTER_ACTION_SELECTOR)) return
      e.preventDefault()
      if (isKeyFromEditableField(e)) {
        target.blur()
        setIsApplyRequested(true)
        return
      }
      handleOnSet()
    },
  })

  const afterDate = dateRange?.after
  const afterTime = timeRange?.after

  const beforeDate = dateRange?.before
  const beforeTime = timeRange?.before

  const afterLabel = t('dateTimeRangeModal.after', 'After').trim()
  const beforeLabel = t('dateTimeRangeModal.before', 'Before').trim()

  return (
    <MangoModal
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose?.()
      }}
      header={t('dateTimeRangeModal.selectDateRange')}
      body={
        <Flex direction="column">
          <PeriodGroup
            label={afterLabel}
            optionalText={t('dateTimeRangeModal.optional', '(optional)')}
          >
            <DateInput
              label={afterLabel}
              value={afterDate}
              mountsFocused
              onDateSet={(val) => setDateRangeValue(PeriodName.After, val)}
            />
            <TimeInput
              label={afterLabel}
              timePeriod={afterTime}
              disabled={!afterDate}
              onTimeSet={(timePeriod) =>
                setTimeRangeValue(PeriodName.After, timePeriod)
              }
            />
          </PeriodGroup>
          <PeriodGroup
            label={beforeLabel}
            optionalText={t('dateTimeRangeModal.optional', '(optional)')}
          >
            <DateInput
              label={beforeLabel}
              value={beforeDate}
              onDateSet={(val) => setDateRangeValue(PeriodName.Before, val)}
            />
            <TimeInput
              label={beforeLabel}
              timePeriod={beforeTime}
              disabled={!beforeDate}
              onTimeSet={(timePeriod) =>
                setTimeRangeValue(PeriodName.Before, timePeriod)
              }
            />
          </PeriodGroup>
        </Flex>
      }
      footer={
        <Flex gap="0.75rem">
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={onClear}
          >
            {t('dateTimeRangeModal.clearButton')}
          </MangoButton>
          <MangoButton
            type="submit"
            variant={MangoButtonVariant.Default_300}
            onClick={handleOnSet}
          >
            {t('dateTimeRangeModal.setDateRangeButton')}
          </MangoButton>
        </Flex>
      }
    />
  )
}
